import {useConfig} from '@aboutyou/config';
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {useTrackMessage} from '@aboutyou/tracking';
import {
  Fade,
  FadeTransition,
  HeightTransition,
  Slide,
} from '@aboutyou/transitions';
import {
  COLORS,
  GenericIconName,
  HEADER_HEIGHT,
  Icon,
  RADIUS,
  RichText,
  SPACING,
  Text,
  UnstyledButton,
  Z_INDEXES,
  desktopAndUpLinaria,
  largeScreensLinaria,
  mediaTabletAndUp,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {useWalletStateManager} from '../../../backend-services/Wallet/utils/useWalletStateManager';
import {MissionBadge} from '../MissionBadge/MissionBadge';
import {ClaimNowButton} from '../shared/ClaimNowButton';
import headerHintBackground from './assets/hint-header-background.png';
import {useSuccessMissionPopup} from './utils/useSuccessMissionPopup';

const DEFAULT_TRANSITION_DURATION = 300;

type SuccessMissionPopupProps = {
  autoCloseDelayMs?: number | null;
  congratulationStageDelayMs?: number | null;
};

export const SuccessMissionPopup: React.FC<SuccessMissionPopupProps> = ({
  autoCloseDelayMs,
  congratulationStageDelayMs,
}) => {
  const {device} = useConfig();
  const trackMessage = useTrackMessage();
  const {
    canShowPopup,
    handleOnClose,
    handleOnUnmount,
    missionSuccessNotification,
    openerPosition,
    RotatingCoins,
    step,
  } = useSuccessMissionPopup({autoCloseDelayMs, congratulationStageDelayMs});
  const {open: openWallet} = useWalletStateManager();

  const {variant} = useIsFeatureActive('ff_missions_v2_rollout', {
    abTest: 'ab_missions_claiming',
  });

  const missionCta = missionSuccessNotification?.congratulationsStage.cta;
  const missionIdentifier = missionSuccessNotification?.missionIdentifier;
  const missionNotificationTrackingKey = missionIdentifier
    ? `${missionIdentifier.missionId}:${missionIdentifier.tierIndex}`
    : undefined;
  const hasRenderedCta = Boolean(missionCta) && variant === 'mic-1';
  const trackedMissionImpressionKeyRef = useRef<string | undefined>(undefined);

  const trackingContext = hasRenderedCta
    ? 'with_cta_button'
    : 'without_cta_button';

  const trackMissionPopupMessage = useCallback(
    (action: 'impression' | 'close' | 'click') => {
      if (!missionSuccessNotification) {
        return;
      }
      trackMessage({
        action,
        content_type: 'notification',
        name: 'mission_achieved',
        context: trackingContext,
        event_scope: 'long',
        object_type: 'mission',
        object_id: missionSuccessNotification.missionIdentifier.missionId,
        object_context: missionSuccessNotification.congratulationsStage.reward,
      });
    },
    [missionSuccessNotification, trackMessage, trackingContext],
  );

  const handleOnNotificationClick = useCallback(() => {
    trackMissionPopupMessage('click');
    handleOnClose();
    openWallet('coins');
  }, [handleOnClose, openWallet, trackMissionPopupMessage]);

  const handleCloseButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      trackMissionPopupMessage('close');
      handleOnClose();
    },
    [handleOnClose, trackMissionPopupMessage],
  );

  useEffect(() => {
    if (!missionNotificationTrackingKey) {
      trackedMissionImpressionKeyRef.current = undefined;
      return;
    }

    if (
      !canShowPopup ||
      !missionSuccessNotification ||
      trackedMissionImpressionKeyRef.current === missionNotificationTrackingKey
    ) {
      return;
    }

    trackedMissionImpressionKeyRef.current = missionNotificationTrackingKey;
    trackMissionPopupMessage('impression');
  }, [
    canShowPopup,
    missionNotificationTrackingKey,
    missionSuccessNotification,
    trackMissionPopupMessage,
  ]);

  const handleCtaClick = useCallback(() => {
    trackMissionPopupMessage('click');
  }, [trackMissionPopupMessage]);

  return (
    <Slide
      direction={device === 'desktop' ? 'down' : 'up'}
      duration={500}
      mounted={canShowPopup}
      onUnmount={handleOnUnmount}
    >
      <Container
        $top={openerPosition?.top ?? 0}
        data-testid="successMissionPopup"
      >
        <Hint>
          <HeaderHint>
            <div>
              <Icon
                asset={GenericIconName.Coins}
                size="XS"
                fill={COLORS['Fill.White']}
              />
              <HeaderTitle size="px10" weight="mediumBold" color="Text.White">
                {missionSuccessNotification?.title}
              </HeaderTitle>
            </div>
            <UnstyledButton
              aria-label="Close success mission popup"
              onClick={handleCloseButtonClick}
            >
              <Icon
                asset={GenericIconName.Close}
                size="XSS"
                fill={COLORS['Fill.White']}
              />
            </UnstyledButton>
          </HeaderHint>
          <BodyHint data-step={step}>
            <Content onClick={handleOnNotificationClick}>
              <CoinImageWrapper>
                <RotatingCoins />
              </CoinImageWrapper>
              <FadeTransition duration={300} uniqueKey={step}>
                {step === 'mission' ? (
                  <BodyHintContent>
                    <ContentTitle size="px14" weight="mediumHeavy">
                      {missionSuccessNotification?.missionStage.title}
                    </ContentTitle>
                    <ProgressBar />
                  </BodyHintContent>
                ) : (
                  <CongratulationBody
                    missionSuccessNotification={missionSuccessNotification}
                    variant={variant}
                  />
                )}
              </FadeTransition>
            </Content>
            {step === 'congratulation' &&
              hasRenderedCta &&
              missionCta &&
              missionIdentifier && (
                <ClaimNowButton
                  Wrapper={ClaimNowButtonWrapper}
                  onClick={handleOnClose}
                  onBeforeClick={handleCtaClick}
                  label={missionCta.label}
                  missionIdentifier={missionIdentifier}
                />
              )}
          </BodyHint>
        </Hint>
      </Container>
    </Slide>
  );
};

type CongratulationBodyProps = {
  missionSuccessNotification: ReturnType<
    typeof useSuccessMissionPopup
  >['missionSuccessNotification'];
  variant?: 'mic-1' | 'mic-2' | 'mic-c';
};

const CongratulationBody: React.FC<CongratulationBodyProps> = ({
  missionSuccessNotification,
  variant,
}) => {
  const congratulationsStage = missionSuccessNotification?.congratulationsStage;

  if (!congratulationsStage) {
    return null;
  }

  if (variant === 'mic-1') {
    return (
      <BodyHintContent>
        <ContentTitle weight="mediumHeavy" size="px14">
          {congratulationsStage.title}
        </ContentTitle>
        <ContentRichText weight="mediumHeavy" size="px10">
          {congratulationsStage.description.text}
        </ContentRichText>
        <StyledMissionBadge
          small
          showCoinsLabel
          reward={congratulationsStage.reward}
        />
      </BodyHintContent>
    );
  }

  if (variant === 'mic-2') {
    return (
      <BodyHintContent>
        <ContentTitle weight="mediumHeavy" size="px14">
          {congratulationsStage.title}
        </ContentTitle>
        <ContentRichText weight="mediumHeavy" size="px12">
          {congratulationsStage.description.text}
        </ContentRichText>
      </BodyHintContent>
    );
  }

  return (
    <BodyHintContent>
      <ContentTitle weight="mediumHeavy" size="px12">
        {congratulationsStage?.title}
      </ContentTitle>
      <ContentRichText weight="mediumHeavy" size="px12">
        {congratulationsStage?.description?.text ?? ''}
      </ContentRichText>
      <StyledMissionBadge
        small
        showCoinsLabel
        reward={congratulationsStage?.reward ?? 0}
      />
    </BodyHintContent>
  );
};

const ClaimNowButtonWrapper: FC<PropsWithChildren> = ({children}) => {
  return (
    <HeightTransition mounted duration={DEFAULT_TRANSITION_DURATION}>
      <Fade mounted duration={DEFAULT_TRANSITION_DURATION + 200}>
        {children}
      </Fade>
    </HeightTransition>
  );
};

const Container = styled.div<{
  $top: number;
}>`
  position: fixed;
  width: calc(100% - 32px);
  left: 16px;
  bottom: 16px;
  z-index: ${Z_INDEXES.SHOP_HINT};

  @media ${mediaTabletAndUp} {
    width: 100%;
    max-width: 412px;
    top: calc(${props => `${props.$top}px`} + ${HEADER_HEIGHT / 2}px + 10px);
    right: 20px;
    bottom: unset;
    left: unset;

    &::before {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      top: -4px;
      left: 120px;
      border-bottom: 5px solid ${COLORS['Coins.Grey']};
    }
  }

  ${desktopAndUpLinaria(`
    &::before {
      left: 42px;
    }
  `)}

  ${largeScreensLinaria(`
    right: 50px;
  `)}
`;

const CoinImageWrapper = styled.div`
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;

  [data-step='congratulation'] & {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent 0%,
        transparent 30%,
        rgba(255, 255, 255, 0.3) 50%,
        transparent 70%,
        transparent 100%
      );
      animation: shimmer 2s infinite;
      pointer-events: none;
      z-index: 1;
    }
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%) skewX(-20deg);
    }
    50%,
    100% {
      transform: translateX(100%) skewX(-20deg);
    }
  }
`;

const Hint = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.2);
`;

const HeaderHint = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 24px;
  padding-inline: ${SPACING['X.Md']};
  background-image: url(${headerHintBackground});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  > div {
    display: flex;
    align-items: center;
    flex: 1;
    gap: 4px;
  }
`;

const HeaderTitle = styled(Text)`
  display: block;
  line-height: 1.6;
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;

  @media ${mediaTabletAndUp} {
    gap: 16px;
  }
`;

const BodyHint = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${SPACING['Y.Md']};
  background-color: ${COLORS['Fill.White']};
  padding: ${SPACING['Y.Md']} ${SPACING['X.Lg']};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const BodyHintContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  gap: 4px;
  padding-right: ${SPACING['X.Lg']};

  [data-step='congratulation'] & {
    gap: 0;
  }
`;

const ContentTitle = styled(Text)`
  display: block;
  line-height: 1.5;
`;

const ContentRichText = styled(RichText)`
  display: block;
  line-height: 1.5;
`;

const ProgressBar = styled.div`
  position: relative;
  width: 100%;
  height: 6px;
  margin: 5px 0;
  border-radius: ${RADIUS.Round};
  background-color: ${COLORS['Fill.Grey.Primary']};
  overflow: hidden;

  @keyframes progressBarAnimation {
    0% {
      transform: scaleX(0.1);
    }
    100% {
      transform: scaleX(1);
    }
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${COLORS['Fill.Grey.Secondary']};
    transform: scaleX(0);
    transform-origin: left center;
    animation: progressBarAnimation 0.3s
      linear(
        0,
        -0.009 7.5%,
        -0.038 15.1%,
        -0.2 39.5%,
        -0.242 48.3%,
        -0.25 53.1%,
        -0.245 57.5%,
        -0.227 61.6%,
        -0.194 65.6%,
        -0.132 70.5%,
        -0.048 75.1%,
        0.197 83.9%,
        0.547 92.2%,
        1
      )
      forwards;

    animation-delay: 450ms;
  }

  [data-step='congratulation'] & {
    margin-top: 12px;

    &:before {
      transform: scaleX(1);
    }
  }
`;

const StyledMissionBadge = styled(MissionBadge)`
  margin-top: 2px;
`;
